import type { AuthContextType } from '@context/auth.context';
import type { AttachmentPayload, TypedSocket } from '@context/socket.context';
import { getAttachmentType, getImageDimensions, getVideoDimensions } from '@helpers/build-attachment';
import type { Attachment } from '@interfaces/attachment';
import type { LocalMessage } from '@interfaces/message';
import { isResponse } from '@services/fetcher';
import fetchConversationTelegramUsers from '@utils/api/fetchConversationTelegramUsers';
import type { LoadDirection } from '@utils/api/fetchCursorMessages';
import fetchCursorMessages from '@utils/api/fetchCursorMessages';
import type { MessagesByDateMeta } from '@utils/api/fetchMessagesByDate';
import fetchMessagesByDate from '@utils/api/fetchMessagesByDate';
import searchMessagesInConversation from '@utils/api/searchMessagesInConversation';
import { omit } from '@utils/iteratees';

import type {
  createSharedSlice,
  MiddleSearchParams,
  MiddleSearchResults,
  ScrollTargetPosition,
  SharedSlice,
} from '../slices/shared';

export const MESSAGE_SLICE = 60;

export const loadConversationMessages: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, id: number, direction?: LoadDirection, cursor?: number) => Promise<void> =
  (_, get) =>
  async (auth, id, direction = 'both', cursor?: number) => {
    const conversation = get().conversations.byId[id].resource;
    if (conversation === null) {
      throw new Error('Trying to load non-existent conversation messages.');
    }

    if (direction === 'before' && conversation.previousCursor === undefined) {
      // We are at the beginning of the list, and the load was triggered by scroll
      return;
    }

    if (direction === 'after' && conversation.nextCursor === undefined && cursor === undefined) {
      // We are at the end of the list, and the load was triggered by scroll
      return;
    }

    if (direction === 'after' && cursor !== undefined && conversation.unread === null) {
      // There is no any new messages to load
      return;
    }

    const setIsLoadingMessages = get().conversations.byId[id].setIsLoadingMessages;

    switch (direction) {
      case 'both': {
        setIsLoadingMessages(true);
        const response = await fetchCursorMessages(auth, id, cursor || conversation.cursor, direction, MESSAGE_SLICE);
        const messages = response.data;
        if (messages.length > 0) {
          get().messages.replaceMessages(messages);
        } else {
          get().messages.replaceChatMessages(id, {});
        }
        get().conversations.updateChatCursors(id, response.meta?.previous_cursor, response.meta?.next_cursor);

        setIsLoadingMessages(false);

        break;
      }
      case 'before': {
        setIsLoadingMessages(true);
        const response = await fetchCursorMessages(auth, id, conversation.previousCursor, direction, MESSAGE_SLICE);
        const messages = response.data;

        // We don't sort here, as we assume that API returned them already sorted ((a.date < b.date) ? -1 : ((a.date > b.date) ? 1 : 0)) || localCompare for ISO date strings
        get().messages.addMessages(messages);
        get().conversations.updateChatPreviousCursor(id, response.meta?.previous_cursor);

        setIsLoadingMessages(false);

        break;
      }
      case 'after': {
        setIsLoadingMessages(true);
        const response = await fetchCursorMessages(
          auth,
          id,
          cursor ?? conversation.nextCursor,
          direction,
          MESSAGE_SLICE,
        );

        const messages = response.data;

        get().messages.addMessages(messages);
        get().conversations.updateChatNextCursor(id, response.meta?.next_cursor);

        setIsLoadingMessages(false);

        break;
      }
    }
  };

export const loadConversationMessagesByDate: (
  ...params: Parameters<typeof createSharedSlice>
) => (
  auth: AuthContextType,
  id: number,
  selectedDate: string,
  timezone: string,
) => Promise<MessagesByDateMeta | undefined> = (_, get) => async (auth, id, selectedDate, timezone) => {
  const conversationEntry = get().conversations.byId[id];
  if (!conversationEntry || conversationEntry.resource === null) {
    throw new Error('Trying to load non-existent conversation messages.');
  }

  const setIsLoadingMessages = conversationEntry.setIsLoadingMessages;

  setIsLoadingMessages(true);

  try {
    const response = await fetchMessagesByDate(auth, id, selectedDate, timezone);

    if (get().activeConversationId !== id) {
      return response.meta;
    }

    get().messages.replaceChatMessageWindow(id, response.data);

    get().conversations.updateChatCursors(id, response.meta?.previous_cursor, response.meta?.next_cursor);

    if (response.meta?.anchor_message_id !== null && response.meta?.anchor_message_id !== undefined) {
      get().focusMessage(id, response.meta.anchor_message_id, 'start');
    }

    return response.meta;
  } finally {
    get().conversations.byId[id]?.setIsLoadingMessages(false);
  }
};

const loadAndReplaceConversationMessages: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, id: number, cursor?: number) => Promise<void> =
  (_, get) => async (auth, id, cursor?: number) => {
    const conversation = get().conversations.byId[id].resource;
    if (conversation === null) {
      throw new Error('Trying to load non-existent conversation messages.');
    }

    const setIsLoadingMessages = get().conversations.byId[id].setIsLoadingMessages;

    setIsLoadingMessages(true);
    const response = await fetchCursorMessages(auth, id, cursor || conversation.cursor, 'both', MESSAGE_SLICE);
    const messages = response.data;
    if (messages.length > 0) {
      get().messages.replaceChatMessages(id, {});
      get().messages.replaceMessages(messages);
    } else {
      get().messages.replaceChatMessages(id, {});
    }
    get().conversations.updateChatCursors(id, response.meta?.previous_cursor, response.meta?.next_cursor);

    setIsLoadingMessages(false);
  };

export const loadAllUnreadMessages: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, conversationId: number) => Promise<void> =
  (_, get, store) => async (auth, conversationId) => {
    let cursor = get().conversations.byId[conversationId].resource?.nextCursor;
    if (cursor === undefined) {
      // if we don't have cursor for next messages, exit immediately
      return;
    }
    const loadMessages = get().loadConversationMessages;

    const unsubscribe = store.subscribe(async (state) => {
      cursor = state.conversations.byId[conversationId].resource?.nextCursor;
    });

    try {
      while (cursor) {
        await loadMessages(auth, conversationId, 'after');
      }
    } finally {
      unsubscribe();
    }
  };

export const loadNewUnreadMessages: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, conversationId: number) => Promise<void> = (_, get) => async (auth, conversationId) => {
  const cursor = get().conversations.byId[conversationId].resource?.cursor;
  const nextCursor = get().conversations.byId[conversationId].resource?.nextCursor;
  const messageIds = get().messages.byConversationId?.[conversationId];
  const loadMessages = get().loadConversationMessages;

  if (cursor === undefined || nextCursor === undefined || messageIds === undefined || messageIds.length === 0) {
    return;
  }

  if (messageIds[messageIds.length - 1] === cursor && nextCursor > cursor) {
    await loadMessages(auth, conversationId, 'after', cursor);
  }
};

export const sendMessage: (
  ...params: Parameters<typeof createSharedSlice>
) => (
  auth: AuthContextType,
  socket: TypedSocket,
  conversationId: number,
  text: string,
  userId: number,
  authorName?: string,
  replyToId?: number,
) => Promise<void> = (_, get) => async (auth, socket, conversationId, text, userId, authorName, replyToId) => {
  // Load newer messages before appending the new message
  const loadUnread = get().loadUnreadMessages;

  await loadUnread(auth, conversationId);

  const nextLocalId = Math.max(...get().messages.allIds, 0) + 1;
  const localMessage: LocalMessage = {
    localId: nextLocalId,
    type: 'text',
    user_id: userId,
    conversation_id: conversationId,
    telegram_user_id: null,
    author_name: authorName !== undefined ? authorName : null,
    author_phone: null,
    body: text,
    created_at: new Date().toISOString(),
    extra_attributes: {},
    reply_to_id: replyToId,
  };

  get().messages.addMessage(localMessage);

  socket.emit('message', {
    conversationId,
    message: localMessage,
  });
};

export const forwardMessage: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, socket: TypedSocket, userId: number, authorName?: string) => Promise<void> =
  (_, get) => async (auth, socket, userId, authorName) => {
    const forwardedMessage = get().forwardedMessage;
    if (!forwardedMessage) {
      return;
    }

    const { fromChatId, messageId, toChatId } = forwardedMessage;
    const message = messageId ? get().messages.byId[messageId] : undefined;

    if (!fromChatId || !toChatId || !message) {
      return;
    }

    // Load newer messages before appending the new message
    const loadUnread = get().loadUnreadMessages;

    await loadUnread(auth, toChatId);

    const nextLocalId = Math.max(...get().messages.allIds, 0) + 1;

    const localMessage: LocalMessage = {
      localId: nextLocalId,
      type: message.type === 'service-message' ? 'text' : message.type,
      user_id: userId,
      conversation_id: toChatId,
      telegram_user_id: null,
      author_name: authorName !== undefined ? authorName : null,
      author_phone: null,
      body: message.body,
      created_at: new Date().toISOString(),
      extra_attributes: message.extra_attributes,
      forward_info: {
        fromChatId,
        fromId: (message.user_id || message.telegram_user_id)!,
        fromTGId: message.telegram_user_id ?? undefined,
        fromMessageId: message.id!,
      },
    };

    get().messages.addMessage(localMessage);

    socket.emit('message', {
      conversationId: toChatId,
      message: localMessage,
    });

    get().exitForwardMode();
  };

export const sendAttachments: (
  ...params: Parameters<typeof createSharedSlice>
) => (
  auth: AuthContextType,
  socket: TypedSocket,
  conversationId: number,
  userId: number,
  attachments: Attachment[],
  caption?: string,
  authorName?: string,
  replyToId?: number,
) => Promise<void> =
  (_, get) => async (auth, socket, conversationId, userId, attachments, caption, authorName, replyToId) => {
    // Load newer messages before appending the new message
    const loadUnread = get().loadUnreadMessages;

    await loadUnread(auth, conversationId);

    const payload: AttachmentPayload[] = [];

    for (const attachment of attachments) {
      const type = getAttachmentType(attachment);
      const nextLocalId = Math.max(...get().messages.allIds, 0) + 1;

      const extra_attributes: Record<string, string | number> = {
        file_id: attachment.blobUrl,
      };

      if (type === 'photo') {
        const dimensions = await getImageDimensions(attachment.blobUrl);
        extra_attributes.width = dimensions.width;
        extra_attributes.height = dimensions.height;
      } else if (type === 'video') {
        const dimensions = await getVideoDimensions(attachment.blobUrl);
        extra_attributes.width = dimensions.width;
        extra_attributes.height = dimensions.height;
        extra_attributes.file_name = attachment.filename;
        extra_attributes.mime_type = attachment.mimeType;
        extra_attributes.file_size = attachment.size;
      } else if (type === 'document' || type === 'audio') {
        extra_attributes.file_name = attachment.filename;
        extra_attributes.mime_type = attachment.mimeType;
        extra_attributes.file_size = attachment.size;
      }

      payload.push({
        localId: nextLocalId,
        filename: attachment.filename,
        type,
        size: attachment.size,
        file: attachment.file,
        caption: caption,
        reply_to_id: replyToId,
      });

      const localMessage: LocalMessage = {
        localId: nextLocalId,
        type,
        user_id: userId,
        telegram_user_id: null,
        author_name: authorName || null,
        conversation_id: conversationId,
        author_phone: null,
        body: caption || null,
        created_at: new Date().toISOString(),
        extra_attributes: type === 'photo' ? [extra_attributes] : extra_attributes,
        reply_to_id: replyToId,
      };

      get().messages.addMessage(localMessage);
    }

    socket.emit('attachments', {
      conversationId: conversationId,
      attachments: payload,
    });
  };

export const openForwardMenu: (
  ...params: Parameters<typeof createSharedSlice>
) => (fromChatId: number, messageId: number) => void = (set) => (fromChatId, messageId) => {
  set(
    (state) => ({
      ...state,
      forwardedMessage: {
        fromChatId,
        messageId,
      },
      isForwardModalOpen: true,
    }),
    undefined,
    'openForwardMenu',
  );
};

export const exitForwardMode: (...params: Parameters<typeof createSharedSlice>) => () => void = (set) => () => {
  set(
    (state) => ({
      ...state,
      forwardedMessage: undefined,
      isForwardModalOpen: false,
    }),
    undefined,
    'exitForwardMode',
  );
};

export const setForwardChat: (...params: Parameters<typeof createSharedSlice>) => (chatId: number) => void =
  (set, get) => (chatId) => {
    set(
      (state) => {
        if (!state.forwardedMessage) {
          return state;
        }

        return {
          ...state,
          forwardedMessage: {
            ...state.forwardedMessage,
            toChatId: chatId,
          },
          isForwardModalOpen: false,
        };
      },
      undefined,
      'setForwardChat',
    );

    get().setActiveConversationId(chatId);
  };

export const fetchActiveConversationMembers: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, signal?: AbortSignal) => void = (set, get) => (auth, signal) => {
  const activeConversationId = get().activeConversationId;
  if (!activeConversationId) {
    set((state) => ({ ...state, activeConversationMembers: undefined }));
    return;
  }
  const requestedConversationId = activeConversationId;

  fetchConversationTelegramUsers(auth, activeConversationId, signal ? { signal } : undefined)
    .then((response) => {
      if (get().activeConversationId !== requestedConversationId) {
        return;
      }

      if (isResponse(response)) {
        // Do we have a case for Response object to handle?
        return;
      }

      set((state) => ({
        ...state,
        activeConversationMembers: response,
      }));
    })
    .catch((error) => {
      if (error?.name === 'AbortError') {
        return;
      }

      console.error('Error fetching conversation members:', error);
      // Optionally, set an error state in the store here
      if (get().activeConversationId === requestedConversationId) {
        set((state) => ({ ...state, activeConversationMembers: undefined }));
      }
    });
};

export const hideLeftColumn: (...params: Parameters<typeof createSharedSlice>) => () => void = (set) => () => {
  set((state) => ({
    ...state,
    isLeftColumnShown: false,
  }));
};

export const showLeftColumn: (...params: Parameters<typeof createSharedSlice>) => () => void = (set) => () => {
  set((state) => ({
    ...state,
    isLeftColumnShown: true,
  }));
};

export const openChatInfo: (...params: Parameters<typeof createSharedSlice>) => () => void = (set) => () => {
  set((state) => ({
    ...state,
    chatInfo: {
      isOpen: true,
    },
  }));
};

export const toggleChatInfo: (...params: Parameters<typeof createSharedSlice>) => () => void = (set) => () => {
  set((state) => ({
    ...state,
    chatInfo: {
      isOpen: !state.chatInfo.isOpen,
    },
  }));
};

function replaceMiddleSearch(
  state: Parameters<typeof createSharedSlice>,
  conversationKey: string,
  searchParams?: MiddleSearchParams,
) {
  const [set] = state;

  return set((state) => {
    const current = state.middleSearch.byConversationKey;
    return {
      ...state,
      middleSearch: {
        byConversationKey:
          searchParams === undefined
            ? omit(current, [conversationKey])
            : {
                ...current,
                [conversationKey]: searchParams,
              },
      },
    };
  });
}

// reducer
function updateMiddleSearchInternal(
  state: Parameters<typeof createSharedSlice>,
  conversationId: number,
  update: Partial<MiddleSearchParams>,
) {
  const [, get] = state;
  const conversationKey = `${conversationId}`;
  const currentSearch = get().middleSearch.byConversationKey[conversationKey];

  const updated = {
    type: 'chat',
    ...currentSearch,
    ...update,
  } satisfies MiddleSearchParams;

  return replaceMiddleSearch(state, conversationKey, updated);
}

function replaceMiddleSearchResults(
  state: Parameters<typeof createSharedSlice>,
  conversationId: number,
  results: MiddleSearchResults,
) {
  return updateMiddleSearchInternal(state, conversationId, {
    fetchingQuery: undefined,
    results,
  });
}

function updateMiddleSearchResults(
  state: Parameters<typeof createSharedSlice>,
  conversationId: number,
  update: MiddleSearchResults,
) {
  const [, get] = state;
  const conversationKey = `${conversationId}`;
  const { results } = get().middleSearch.byConversationKey[conversationKey] || {};
  const prevQuery = results?.query || '';

  if (update.query !== prevQuery) {
    return replaceMiddleSearchResults(state, conversationId, update);
  }

  // TODO: make unique found results
  return replaceMiddleSearchResults(state, conversationId, update);
}

export const openMiddleSearch: (...params: Parameters<typeof createSharedSlice>) => () => void =
  (set, get, store) => () => {
    const conversationId = get().activeConversationId;
    if (!conversationId) {
      return;
    }

    return updateMiddleSearchInternal([set, get, store], conversationId, {});
  };

export const closeMiddleSearch: (...params: Parameters<typeof createSharedSlice>) => () => void =
  (set, get, store) => () => {
    const conversationId = get().activeConversationId;
    if (!conversationId) {
      return;
    }

    return replaceMiddleSearch([set, get, store], `${conversationId}`, undefined);
  };

export const updateMiddleSearch: (
  ...params: Parameters<typeof createSharedSlice>
) => (conversationId: number, update: Partial<Omit<MiddleSearchParams, 'results'>>) => void =
  (set, get, store) => (conversationId, update) => {
    updateMiddleSearchInternal([set, get, store], conversationId, update);
  };

export const resetMiddleSearch: (...params: Parameters<typeof createSharedSlice>) => () => void =
  (set, get, store) => () => {
    const conversationId = get().activeConversationId;
    if (!conversationId) {
      return;
    }

    return replaceMiddleSearch([set, get, store], `${conversationId}`, {
      type: 'chat',
    });
  };

export const performMiddleSearch: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, conversationId: number, query?: string) => Promise<void> =
  (set, get, store) => async (auth, conversationId, query) => {
    let currentSearch = get().middleSearch.byConversationKey[`${conversationId}`];

    if (!currentSearch) {
      updateMiddleSearchInternal([set, get, store], conversationId, {});
    }
    currentSearch = get().middleSearch.byConversationKey[`${conversationId}`]!;

    const shouldHaveQuery = currentSearch.type === 'chat';
    if (shouldHaveQuery && !query) {
      updateMiddleSearchInternal([set, get, store], conversationId, { fetchingQuery: undefined });
      return;
    }

    updateMiddleSearchInternal([set, get, store], conversationId, { fetchingQuery: query });

    try {
      const result = await searchMessagesInConversation(auth, conversationId, query!);
      if (!result) {
        return;
      }

      currentSearch = get().middleSearch.byConversationKey[`${conversationId}`]!;
      const hasSearchChanged = currentSearch?.fetchingQuery !== query;
      if (!currentSearch || hasSearchChanged) {
        return;
      }

      updateMiddleSearchResults([set, get, store], conversationId, {
        query: query || '',
        foundMessages: result,
      });
    } catch (error) {
      console.error('Failed to search messages:', error);
      updateMiddleSearchInternal([set, get, store], conversationId, { fetchingQuery: undefined });
    }
  };

export const selectSearchResult: (
  ...params: Parameters<typeof createSharedSlice>
) => (auth: AuthContextType, conversationId: number, messageId: number) => Promise<void> =
  (set, get, store) => async (auth, conversationId, messageId) => {
    const loadMessages = loadAndReplaceConversationMessages(set, get, store);
    await loadMessages(auth, conversationId, messageId);
    get().focusMessage(conversationId, messageId);
  };

const updateFocusedMessageInternal: (
  state: Parameters<typeof createSharedSlice>,
) => (update: Partial<SharedSlice['focusedMessage']> | undefined) => void = (state) => (update) => {
  const [set] = state;
  if (!update) {
    return set({
      focusedMessage: undefined,
    });
  }

  return set((state) => ({
    focusedMessage: {
      ...state.focusedMessage,
      ...update,
    },
  }));
};

let blurTimeout: number | undefined;
let focusVersion = 0;
const FOCUS_DURATION = 1500;

export const focusMessage: (
  ...params: Parameters<typeof createSharedSlice>
) => (conversationId: number, messageId: number, scrollTargetPosition?: ScrollTargetPosition) => void =
  (set, get, store) => (conversationId, messageId, scrollTargetPosition) => {
    const state: Parameters<typeof createSharedSlice> = [set, get, store];
    const updateFocusedMessage = updateFocusedMessageInternal(state);

    if (blurTimeout) {
      clearTimeout(blurTimeout);
      blurTimeout = undefined;
    }

    blurTimeout = window.setTimeout(() => {
      updateFocusedMessage(undefined);
    }, FOCUS_DURATION);

    updateFocusedMessage({
      conversationId,
      messageId,
      focusVersion: ++focusVersion,
      scrollTargetPosition,
    });
  };
